import React, { FunctionComponent, Suspense, lazy, useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import './App.scss';
import { Channel } from 'features/channel/Channel';
import { ChannelHome } from 'features/channel/components/ChannelHome';
import { ChannelHomeDisabled } from 'features/channel/components/ChannelHomeDisabled';
import { ChannelNetworkView } from 'features/channel/components/ChannelNetwork/ChannelNetworkView';
import { AnimatePresence } from 'framer-motion';
import { Error404 } from 'components/ErrorPages/Error404';
import { ErrorEmbed } from 'components/ErrorPages/ErrorEmbed';
import { ErrorDeleted } from 'components/ErrorPages/ErrorDeleted';
import { Loader } from 'components/Loader';
import { useBannedChannelChecker } from 'hooks/useBannedChannelChecker';
import { useBackButtonListener } from 'hooks/useBackButtonListener';
import { useAppSelector } from 'hooks/redux';

// Secondary / heavy routes are code-split so they leave the initial bundle.
// The channel home routes (ChannelHome / ChannelHomeDisabled / ChannelNetworkView)
// and the Channel layout stay eager — they're the LCP path, so we don't want to
// add a chunk round-trip to first paint. Detail pages, chat, and the secondary
// tabs (which pull players, the emoji/gif pickers, etc.) are lazy.
const Event = lazy(() =>
  import('features/events/Event').then((m) => ({ default: m.Event }))
);
const Recording = lazy(() =>
  import('features/recordings/Recording').then((m) => ({
    default: m.Recording
  }))
);
const Collection = lazy(() =>
  import('features/collections/Components/Collection').then((m) => ({
    default: m.Collection
  }))
);
const ChannelChat = lazy(() =>
  import('features/channel/components/ChannelChat').then((m) => ({
    default: m.ChannelChat
  }))
);
const ChannelCrowd = lazy(() =>
  import('features/channel/components/ChannelCrowd').then((m) => ({
    default: m.ChannelCrowd
  }))
);
const ChannelAbout = lazy(() =>
  import('features/channel/components/ChannelAbout').then((m) => ({
    default: m.ChannelAbout
  }))
);
const ChannelEvents = lazy(() =>
  import('features/channel/components/ChannelEvents').then((m) => ({
    default: m.ChannelEvents
  }))
);
const ChannelRecordings = lazy(() =>
  import('features/channel/components/ChannelRecordings').then((m) => ({
    default: m.ChannelRecordings
  }))
);
const ChannelCollections = lazy(() =>
  import('features/channel/components/ChannelCollections').then((m) => ({
    default: m.ChannelCollections
  }))
);
const ChannelNetwork = lazy(() =>
  import('features/channel/components/ChannelNetwork').then((m) => ({
    default: m.ChannelNetwork
  }))
);
const ChannelEmbed = lazy(() =>
  import('features/channel/components/ChannelEmbed').then((m) => ({
    default: m.ChannelEmbed
  }))
);
const CurrentlyLiveWidget = lazy(() =>
  import('features/channel/components/CurrentlyLiveWidget').then((m) => ({
    default: m.CurrentlyLiveWidget
  }))
);
const InstantEvent = lazy(() =>
  import('features/events/EventCreator/InstantEvent').then((m) => ({
    default: m.InstantEvent
  }))
);

export const AnimatedRouteSwitch: FunctionComponent = () => {
  const location = useLocation();
  const channel = useAppSelector((state) => state.channel.channelDetails);
  const channelHomepageEnabled = useAppSelector(
    (state) => state.channel.channelDetails?.channelHomepageEnabled
  );
  const networkOwner = useAppSelector(
    (state) => state.channel.channelDetails?.networkOwner
  );

  useBackButtonListener();
  useBannedChannelChecker();

  useEffect(() => {
    return () => {
      window.history.replaceState({}, document.title);
    };
  }, []);

  if (networkOwner) {
    return (
      <AnimatePresence initial={false}>
        <Suspense fallback={<Loader fillComponent={true} />}>
          <Routes location={location} key={location.pathname}>
            <Route element={<Channel />}>
              <Route path="/" element={<ChannelNetworkView />} />
            </Route>
            <Route path="*" element={<Error404 />} />
          </Routes>
        </Suspense>
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence initial={false}>
      <Suspense fallback={<Loader fillComponent={true} />}>
        <Routes location={location} key={location.pathname}>
          {channelHomepageEnabled ? (
            <Route element={<Channel />}>
              <Route path="/" element={<ChannelHome />} />
              {channel?.networkAvailable && (
                <Route path="/network" element={<ChannelNetwork />} />
              )}
              <Route path="/about" element={<ChannelAbout />} />
              <Route path="/events" element={<ChannelEvents />} />
              <Route path="/recordings" element={<ChannelRecordings />} />
              <Route
                path="/recordings/page/:page"
                element={<ChannelRecordings />}
              />
              <Route path="/collections" element={<ChannelCollections />} />
              <Route
                path="/collections/page/:page"
                element={<ChannelCollections />}
              />
              <Route
                path="/collections/deleted"
                element={<ErrorDeleted type={'recording'} />}
              />
              <Route
                path="/collections/not-found"
                element={<ErrorEmbed type={'recording'} />}
              />
              <Route path="/collections/:id" element={<Collection />} />
              <Route
                path="/collections/:id/page/:page"
                element={<Collection />}
              />
            </Route>
          ) : (
            <Route element={<Channel />}>
              <Route path="/" element={<ChannelHomeDisabled />} />
            </Route>
          )}
          <Route path="/chat" element={<ChannelChat />} />
          <Route path="/crowd" element={<ChannelCrowd />} />
          <Route path="/embed" element={<ChannelEmbed />} />
          <Route path="/widgets/live-now" element={<CurrentlyLiveWidget />} />
          <Route path="/events/:id" element={<Event />} />
          <Route path="/events/instant" element={<InstantEvent />} />
          <Route
            path="/events/deleted"
            element={<ErrorDeleted type={'event'} />}
          />
          <Route
            path="/recordings/not-found"
            element={<ErrorEmbed type={'recording'} />}
          />
          <Route
            path="/recordings/deleted"
            element={<ErrorDeleted type={'recording'} />}
          />
          <Route path="/recordings/:id" element={<Recording />}>
            <Route path="/recordings/:id/embed" element={<Recording />} />
          </Route>
          <Route path="*" element={<Error404 />} />
        </Routes>
      </Suspense>
    </AnimatePresence>
  );
};
